import styled, { keyframes, css } from "styled-components";
// Path assumes this file lives at
// src/components/sections/WelcomeScreen/Assessment/AssessmentStepShell.styles.js
import { DIET_C, DIET_CSS_VARS } from "../../../common/colors";

// ═══════════════════════════════════════════════════════════════════════════════
// ASSESSMENT STEP SHELL — DESIGN TOKENS
// Shared chrome for every wizard screen: brand header, progress bar + step
// counter, question title/description, content slot, Previous/Next footer.
// Individual steps (GoalStep, etc.) only supply the content in between.
// ═══════════════════════════════════════════════════════════════════════════════

export const kFadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0);     }
`;

const motion = (animCss) => css`
  @media (prefers-reduced-motion: no-preference) { ${animCss} }
`;

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION ROOT
// ═══════════════════════════════════════════════════════════════════════════════

export const Section = styled.section`
  ${DIET_CSS_VARS}

  --r-sm: 10px;
  --r:    14px;

  --sp-1: 0.25rem;
  --sp-2: 0.5rem;
  --sp-3: 0.75rem;
  --sp-4: 1rem;
  --sp-5: 1.25rem;
  --sp-6: 1.5rem;
  --sp-8: 2rem;

  --t-fast: 0.15s ease;
  --t:      0.22s ease;

  position: relative;
  background: var(--c-bg);
  min-height: 100%;
`;
Section.displayName = "Section";

export const AppShell = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: var(--sp-5) var(--sp-4) var(--sp-8);

  @media (max-width: 40em) {
    padding: var(--sp-3) var(--sp-3) var(--sp-6);
  }
`;
AppShell.displayName = "AppShell";

// ═══════════════════════════════════════════════════════════════════════════════
// BRAND HEADER — matches Welcome.styles.js's Header/Brand for visual continuity
// ═══════════════════════════════════════════════════════════════════════════════

export const Header = styled.header`
  background: var(--c-surface);
  border: 1px solid ${DIET_C.secondary}55;
  border-radius: var(--r-sm);
  padding: var(--sp-3) var(--sp-5);
  margin-bottom: var(--sp-4);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

  @media (max-width: 40em) {
    padding: var(--sp-3) var(--sp-4);
  }
`;
Header.displayName = "Header";

export const Brand = styled.span`
  font-weight: 700;
  font-size: 1.05rem;
  color: var(--c-primary);
`;
Brand.displayName = "Brand";

// ═══════════════════════════════════════════════════════════════════════════════
// PROGRESS BAR + STEP COUNTER
// ═══════════════════════════════════════════════════════════════════════════════

export const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-4);
  margin-bottom: var(--sp-6);
`;
ProgressRow.displayName = "ProgressRow";

export const ProgressTrack = styled.div`
  flex: 1;
  height: 10px;
  border-radius: 999px;
  background: ${DIET_C.secondary}33;
  overflow: hidden;
`;
ProgressTrack.displayName = "ProgressTrack";

export const ProgressFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: var(--c-primary);
  width: ${({ $percent = 0 }) => Math.min(100, Math.max(0, $percent))}%;
  transition: width 0.4s ease;
`;
ProgressFill.displayName = "ProgressFill";

export const StepCounter = styled.span`
  flex: none;
  font-size: 0.85rem;
  color: var(--c-text-muted);
  white-space: nowrap;
`;
StepCounter.displayName = "StepCounter";

// ═══════════════════════════════════════════════════════════════════════════════
// QUESTION HEADER
// ═══════════════════════════════════════════════════════════════════════════════

export const QuestionTitle = styled.h1`
  color: var(--c-text);
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0 0 var(--sp-2);
  ${motion(css`animation: ${kFadeUp} 0.4s ease both;`)}

  @media (max-width: 40em) {
    font-size: 1.25rem;
  }
`;
QuestionTitle.displayName = "QuestionTitle";

export const QuestionDesc = styled.p`
  color: var(--c-text-muted);
  font-size: 0.95rem;
  margin: 0 0 var(--sp-6);
  ${motion(css`animation: ${kFadeUp} 0.4s ease both;`)}
  animation-delay: 40ms;

  @media (max-width: 40em) {
    font-size: 0.88rem;
    margin-bottom: var(--sp-5);
  }
`;
QuestionDesc.displayName = "QuestionDesc";

// ═══════════════════════════════════════════════════════════════════════════════
// CONTENT SLOT
// ═══════════════════════════════════════════════════════════════════════════════

export const StepContent = styled.div`
  margin-bottom: var(--sp-8);
`;
StepContent.displayName = "StepContent";

// ═══════════════════════════════════════════════════════════════════════════════
// FOOTER NAV
// ═══════════════════════════════════════════════════════════════════════════════

export const FooterNav = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--sp-4);

  @media (max-width: 40em) {
    flex-direction: column-reverse;

    > * {
      width: 100%;
    }
  }
`;
FooterNav.displayName = "FooterNav";

const navButtonBase = css`
  border-radius: var(--r-sm);
  padding: 13px 28px;
  font-size: 0.98rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform var(--t-fast), background var(--t-fast), opacity var(--t-fast);

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 3px solid var(--c-accent);
    outline-offset: 2px;
  }
`;

export const PreviousButton = styled.button`
  ${navButtonBase}
  background: var(--c-surface);
  color: var(--c-text);
  border: 1px solid ${DIET_C.secondary}88;

  &:hover:not(:disabled) {
    background: ${DIET_C.secondary}22;
  }
`;
PreviousButton.displayName = "PreviousButton";

export const NextButton = styled.button`
  ${navButtonBase}
  background: var(--c-primary);
  color: #fff;
  border: none;

  &:hover:not(:disabled) {
    background: #26692a;
    transform: translateY(-1px);
  }
`;
NextButton.displayName = "NextButton";